import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import EnterpriseList from "../Components/EnterpriseList";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCheck, FaTimes,  FaEye, FaEyeSlash } from "react-icons/fa";
import { TbLoader2 } from "react-icons/tb";

// ---------------------------------
// Utilities
// ---------------------------------
const classNames = (...xs) => xs.filter(Boolean).join(" ");
const toLocal = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

const statusLabel = (s) => (s === 1 ? "In Use" : s === 0 ? "Used" : "Provision");
const statusClass = (s) =>
  s === 1
    ? "bg-green-100 text-green-700"
    : s === 0
    ? "bg-slate-200 text-slate-700"
    : "bg-amber-100 text-amber-700";

function escapeCSV(s) {
  const v = String(s ?? "");
  return /[",\n]/.test(v) ? '"' + v.replaceAll('"', '""') + '"' : v;
}

function downloadCSV(rows, filename = "enterprises.csv") {
  const headers = [
    "Default",
    "Status(0/1/2)",
    "Status Label",
    "ID",
    "Email",
    "Phone",
    "Password",
    "Created At",
    "Last Sync",
    "Total Devices",
    "QR Link",
    "Related Type",
    "Related Name",
    "Org Name",
    "Code",
  ];
  const out = [headers.join(",")].concat(
    rows.map((e) =>
      [
        e.__isDefault ? "YES" : "",
        e.status,
        statusLabel(e.status),
        escapeCSV(e.id),
        escapeCSV(e.email),
        escapeCSV(e.phone),
        escapeCSV(e.password),
        escapeCSV(toLocal(e.createdAt)),
        escapeCSV(toLocal(e.lastSyncAt)),
        e.totalDevices,
        escapeCSV(e.qrLink),
        escapeCSV(e.relatedType),
        escapeCSV(e.relatedName),
        escapeCSV(e.orgName),
        escapeCSV(e.code),
      ].join(",")
    )
  );
  const blob = new Blob(["\uFEFF" + out.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Highlight rule
function shouldHighlightRow(e) {
  return e.status === 1 && Number(e.totalDevices) > 300;
}

// ---------------------------------
// Mock chart data (retain existing logic)
// ---------------------------------
const MOCK_DEACTIVATIONS = [
  "2025-01-15", "2025-01-21", "2025-02-02", "2025-02-19",
  "2025-03-05", "2025-03-05", "2025-03-18",
  "2025-04-01", "2025-04-15", "2025-04-30",
  "2025-05-07",
  "2025-06-11", "2025-06-27",
  "2025-07-03", "2025-07-19", "2025-07-28",
  "2025-08-04",
  "2025-09-09",
  "2025-10-01", "2025-10-01", "2025-10-09"
];

function groupByMonth(dates) {
  const map = new Map();
  dates.forEach((d) => {
    const month = d.slice(0, 7); // YYYY-MM
    map.set(month, (map.get(month) || 0) + 1);
  });
  return Array.from(map.entries()).map(([label, count]) => ({ label, count }));
}

function groupByDayOfMonth(dates, month) {
  const inMonth = dates.filter((d) => d.startsWith(month));
  const map = new Map();
  inMonth.forEach((d) => {
    const day = d.slice(8, 10);
    map.set(day, (map.get(day) || 0) + 1);
  });
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const out = [];
  for (let i = 1; i <= daysInMonth; i++) {
    const dd = String(i).padStart(2, "0");
    out.push({ label: dd, count: map.get(dd) || 0 });
  }
  return out;
}

// ---------------------------------
// KPI Component
// ---------------------------------

function KPI({
  title, // e.g. "Total Enterprises"
  value, // e.g. total_entperises
  subtitleLeft, // e.g. "Company = 800"
  subtitleRight, // e.g. "Retailer = 400"
  tone = "neutral",
  loading = false,
}) {
  const toneColors = {
    success: "text-green-700 bg-green-50 border-green-200",
    danger: "text-rose-700 bg-rose-50 border-rose-200",
    warning: "text-amber-700 bg-amber-50 border-amber-200",
    cyan: "text-cyan-700 bg-cyan-50 border-cyan-200",
    neutral: "text-indigo-700 bg-indigo-50 border-indigo-200",
    amber: "text-yellow-700 bg-yellow-50 border-yellow-200",
  };

  const toneClass = toneColors[tone] || toneColors.neutral;

  return (
    <div
      className={classNames(
        "rounded-2xl p-4 flex flex-col items-center justify-between border shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5",
        toneClass
      )}
    >
      {/* Title */}
       <div className="w-full flex items-center justify-between px-1 pb-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide ">
            {title}
          </h3>

           {loading ? (
              <span className=" inline-block w-5 h-5 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin"></span>
            ) : (
              <h3 className="text-xl font-semibold">{value}</h3>
            )}
        </div>


      {/* Subtotals Row */}
      <div className="w-full flex justify-between text-sm text-slate-700 font-medium">
        <span>{subtitleLeft}</span>
        <span>{subtitleRight}</span>
      </div>
    </div>
  );
}




// ---------------------------------
// Main App Component
// ---------------------------------
export default function EnterpriseUI() {
  const [enterprises, setEnterprises] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [defaultId, setDefaultId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Count 
  const [counts, setCounts] = useState(null);
  const navigate = useNavigate();


  const [granularity, setGranularity] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState(() =>
    new Date().toISOString().slice(0, 7)
  );
  const [monthPicker, setMonthPicker] = useState(() =>
    new Date().toISOString().slice(0, 7)
  );

  useEffect(() => {
  const fetchCounts = async () => {
    try {
      const res = await axios.get("http://spillas.com/api/v1/ama-enterprises/counts");
      setCounts(res.data);
    } catch (err) {
      console.error("Failed to fetch counts:", err);
    }
  };

  fetchCounts();
}, []);


  // Fetch data from API
  useEffect(() => {
    const fetchEnterprises = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `https://spillas.com:3055/enterprises?page=${page}&per_page=${perPage}`
        );
        const { enterprises: data, total_pages } = res.data;

        const normalized = data.map((e) => ({
          id: e.id,
          name: e.enterprise_name,
          createdAt: e.created_at,
          lastSyncAt: e.last_sync,
          role:e.role,
          totalDevices: e.device_count ?? 0,
          qrLink: e.qr_url ? `https://spillas.com${e.qr_url}` : "#",
          relatedType: e.company_name ? "company" : "retailer",
          relatedName: e.company_name || e.retailer_name || "-",
          orgName: e.business_name || "-",
          code: e.retailer_code || e.zcompanyid || "-",
          status: Number(e.default_enterprise) || 0,
          email: e.email || "-",
          phone: e.mobile || "-",
          password: e.password ? "••••••••" : "-",
          __isDefault: e.default_enterprise === "1",
        }));

        setEnterprises(normalized);
        setTotalPages(total_pages || 1);
      } catch (err) {
        console.error("Failed to fetch enterprises:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEnterprises();
  }, [page, perPage]);

  const filtered = useMemo(() => {
    return enterprises
      .map((e) => ({ ...e, __isDefault: e.id === defaultId }))
      .filter((e) => {
        const matchText = [
          e.id,
          e.relatedName,
          e.relatedType,
          e.orgName,
          e.code,
          e.email,
          e.phone,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query.toLowerCase());

        const matchStatus =
          statusFilter === "all"
            ? true
            : statusFilter === "inuse"
            ? e.status === 1
            : statusFilter === "used"
            ? e.status === 0
            : e.status === 2;

        return matchText && matchStatus;
      });
  }, [enterprises, query, statusFilter, defaultId]);

  const summary = useMemo(() => {
    const base = enterprises;
    const by = (pred) => base.filter(pred).length;
    return {
      total: base.length,
      inuse: by((e) => e.status === 1),
      used: by((e) => e.status === 0),
      provision: by((e) => e.status === 2),
      companyInuse: by((e) => e.relatedType === "company" && e.status === 1),
      companyUsed: by((e) => e.relatedType === "company" && e.status === 0),
      companyProvision: by((e) => e.relatedType === "company" && e.status === 2),
      retailerInuse: by((e) => e.relatedType === "retailer" && e.status === 1),
      retailerUsed: by((e) => e.relatedType === "retailer" && e.status === 0),
      retailerProvision: by((e) => e.relatedType === "retailer" && e.status === 2),
    };
  }, [enterprises]);

  const monthSeries = useMemo(() => groupByMonth(MOCK_DEACTIVATIONS), []);
  const daySeries = useMemo(
    () => groupByDayOfMonth(MOCK_DEACTIVATIONS, selectedMonth),
    [selectedMonth]
  );

  function requestSetDefault(id) {
    setConfirmId(id);
  }
  function confirmSetDefault() {
    if (confirmId) {
      setDefaultId(confirmId);
      setConfirmId(null);
    }
  }
  function cancelSetDefault() {
    setConfirmId(null);
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] p-2">
      {/* Global Styles */}
      <style>{`
        :root{
          --bg:#f8fafc; --panel:#ffffff; --panel-2:#f1f5f9; --text:#0f172a;
          --muted:#475569; --brand:#4f46e5; --accent:#06b6d4; --success:#16a34a;
          --danger:#dc2626; --warning:#f59e0b; --card:#ffffff; --border:rgba(15,23,42,0.08);
          --shadow: 0 8px 18px rgba(2,6,23,0.08);
        }
        * { font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans'; }
        .card { background: linear-gradient(180deg, var(--panel), var(--panel-2)); box-shadow: var(--shadow); border: 1px solid var(--border); }
        .chip { border:1px solid var(--border); }
        .grid-auto { display:grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem; }
        .btn { transition: transform .06s ease; } .btn:active { transform: translateY(1px); }
        .table-wrap { overflow: auto; } .table { min-width: 1200px; } .muted { color: var(--muted); }
        .badge-default { background: #e0e7ff; color: #3730a3; }
        .bg-red-soft { background: #fee2e2; }
      `}</style>

       <div className="pt-0 pb-3">
          <button
            className="bg-blue-500 hover:bg-blue-400 cursor-pointer text-white px-2 py-1 rounded-md flex items-center gap-1"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft /> Go Back
          </button>
        </div>

      {/* Header */}
      <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            Manage Enterprise
          </h1>
          {/* <p className="text-sm muted mt-1">
            Track status (1=In Use, 0=Used, 2=Provision), default selection, and key details
          </p> */}
        </div>
        {/* <div className="flex flex-wrap gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by ID, email, phone, code..."
            className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] placeholder:opacity-60"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            title="Filter by status"
          >
            <option value="all">All</option>
            <option value="inuse">In Use (1)</option>
            <option value="used">Used (0)</option>
            <option value="provision">Provision (2)</option>
          </select>
          <button
            onClick={() => downloadCSV(filtered)}
            className="btn px-3 py-2 rounded-xl bg-[var(--brand)] text-white"
          >
            Export CSV
          </button>
        </div> */}
      </header>

      {/* KPI Cards */}
      <section
  className="
    grid 
    grid-cols-2 
    sm:grid-cols-2 
    md:grid-cols-3 
    lg:grid-cols-5 
    gap-4 
    mb-6
  "
>
  <KPI
    title="Total"
    value={counts?.total_entperises}
    // subtitleLeft={`Company = ${counts?.total_entperises_company}`}
    // subtitleRight={`Retailer = ${counts?.total_entperises_retailers}`}
    tone="neutral"
    loading={loading}
  />

  <KPI
    title="Default"
    value={counts ? counts.total_entperises_default : 0}
    // subtitleLeft={`Company = ${counts?.total_default_role_2}`}
    // subtitleRight={`Retailer = ${counts?.total_default_role_5}`}
    tone="warning"
    loading={loading}
  />

  <KPI
    title="Used"
    value={counts ? counts.total_entperises_used : 0}
    // subtitleLeft={`Company = ${counts?.total_entperises_used_company}`}
    // subtitleRight={`Retailer = ${counts?.total_entperises_used_retailer}`}
    tone="cyan"
    loading={loading}
  />

  <KPI
    title="Provision"
    value={counts ? counts.total_entperises_prov : 0}
    // subtitleLeft={`Company = ${counts?.total_entperises_prov_company}`}
    // subtitleRight={`Retailer = ${counts?.total_entperises_prov_retailer}`}
    tone="amber"
    loading={loading}
  />

  <KPI
    title="Blocked"
    value={counts ? counts.total_entperises_all_block : 0}
    // subtitleLeft={`Company = ${counts?.total_entperises_company_block}`}
    // subtitleRight={`Retailer = ${counts?.total_entperises_retailer_block}`}
    tone="danger"
    loading={loading}
  />
</section>

      {/* Chart Panel */}
      {/* <section className="card rounded-2xl p-5 mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium">Deactivated Enterprises</h2>
            <p className="text-sm muted">Visualize counts by month or by day within a selected month.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="chip rounded-xl px-2 py-1 text-xs flex items-center gap-2 bg-[var(--panel)]">
              <span className="opacity-75">View</span>
              <button
                className={classNames(
                  "btn px-2 py-1 rounded-lg",
                  granularity === "month" ? "bg-[var(--brand)] text-white" : "bg-[var(--panel-2)]"
                )}
                onClick={() => setGranularity("month")}
              >
                Month
              </button>
              <button
                className={classNames(
                  "btn px-2 py-1 rounded-lg",
                  granularity === "day" ? "bg-[var(--brand)] text-white" : "bg-[var(--panel-2)]"
                )}
                onClick={() => setGranularity("day")}
              >
                Day
              </button>
            </div>

            {granularity === "day" ? (
              <input
                type="month"
                value={monthPicker}
                onChange={(e) => {
                  setMonthPicker(e.target.value);
                  setSelectedMonth(e.target.value);
                }}
                className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                title="Pick month"
              />
            ) : null}
          </div>
        </div>

        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={granularity === "month" ? monthSeries : daySeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,6,23,0.1)" />
              <XAxis dataKey="label" tick={{ fill: "#475569" }} stroke="#cbd5e1" />
              <YAxis allowDecimals={false} tick={{ fill: "#475569" }} stroke="#cbd5e1" />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }} />
              <Bar dataKey="count" fill="var(--accent)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section> */}

      <EnterpriseList/>
    </div>
  );
}

// import React, { useEffect, useMemo, useState } from "react";
// import axios from "axios";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   CartesianGrid,
//   ResponsiveContainer,
// } from "recharts";

// // ---------------------------------
// // Utilities
// // ---------------------------------
// const classNames = (...xs) => xs.filter(Boolean).join(" ");
// const toLocal = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

// const statusLabel = (s) => (s === 1 ? "In Use" : s === 0 ? "Used" : "Provision");
// const statusClass = (s) =>
//   s === 1
//     ? "bg-green-100 text-green-700"
//     : s === 0
//     ? "bg-slate-200 text-slate-700"
//     : "bg-amber-100 text-amber-700";

// function escapeCSV(s) {
//   const v = String(s ?? "");
//   return /[",\n]/.test(v) ? '"' + v.replaceAll('"', '""') + '"' : v;
// }

// function downloadCSV(rows, filename = "enterprises.csv") {
//   const headers = [
//     "Default",
//     "Status(0/1/2)",
//     "Status Label",
//     "ID",
//     "Email",
//     "Phone",
//     "Password",
//     "Created At",
//     "Last Sync",
//     "Total Devices",
//     "QR Link",
//     "Related Type",
//     "Related Name",
//     "Org Name",
//     "Code",
//   ];
//   const out = [headers.join(",")].concat(
//     rows.map((e) =>
//       [
//         e.__isDefault ? "YES" : "",
//         e.status,
//         statusLabel(e.status),
//         escapeCSV(e.id),
//         escapeCSV(e.email),
//         escapeCSV(e.phone),
//         escapeCSV(e.password),
//         escapeCSV(toLocal(e.createdAt)),
//         escapeCSV(toLocal(e.lastSyncAt)),
//         e.totalDevices,
//         escapeCSV(e.qrLink),
//         escapeCSV(e.relatedType),
//         escapeCSV(e.relatedName),
//         escapeCSV(e.orgName),
//         escapeCSV(e.code),
//       ].join(",")
//     )
//   );
//   const blob = new Blob(["\uFEFF" + out.join("\n")], {
//     type: "text/csv;charset=utf-8;",
//   });
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.href = url;
//   a.download = filename;
//   a.click();
//   URL.revokeObjectURL(url);
// }

// // Highlight rule
// function shouldHighlightRow(e) {
//   return e.status === 1 && Number(e.totalDevices) > 300;
// }

// // ---------------------------------
// // Mock chart data (retain existing logic)
// // ---------------------------------
// const MOCK_DEACTIVATIONS = [
//   "2025-01-15", "2025-01-21", "2025-02-02", "2025-02-19",
//   "2025-03-05", "2025-03-05", "2025-03-18",
//   "2025-04-01", "2025-04-15", "2025-04-30",
//   "2025-05-07",
//   "2025-06-11", "2025-06-27",
//   "2025-07-03", "2025-07-19", "2025-07-28",
//   "2025-08-04",
//   "2025-09-09",
//   "2025-10-01", "2025-10-01", "2025-10-09"
// ];

// function groupByMonth(dates) {
//   const map = new Map();
//   dates.forEach((d) => {
//     const month = d.slice(0, 7); // YYYY-MM
//     map.set(month, (map.get(month) || 0) + 1);
//   });
//   return Array.from(map.entries()).map(([label, count]) => ({ label, count }));
// }

// function groupByDayOfMonth(dates, month) {
//   const inMonth = dates.filter((d) => d.startsWith(month));
//   const map = new Map();
//   inMonth.forEach((d) => {
//     const day = d.slice(8, 10);
//     map.set(day, (map.get(day) || 0) + 1);
//   });
//   const [y, m] = month.split("-").map(Number);
//   const daysInMonth = new Date(y, m, 0).getDate();
//   const out = [];
//   for (let i = 1; i <= daysInMonth; i++) {
//     const dd = String(i).padStart(2, "0");
//     out.push({ label: dd, count: map.get(dd) || 0 });
//   }
//   return out;
// }

// // ---------------------------------
// // KPI Component
// // ---------------------------------
// function KPI({ title, value, tone = "neutral" }) {
//   const color =
//     tone === "success"
//       ? "text-green-700"
//       : tone === "danger"
//       ? "text-red-700"
//       : tone === "warning"
//       ? "text-amber-700"
//       : "text-cyan-700";
//   const bg =
//     tone === "success"
//       ? "bg-green-100"
//       : tone === "danger"
//       ? "bg-red-100"
//       : tone === "warning"
//       ? "bg-amber-100"
//       : "bg-cyan-100";
//   return (
//     <div className={classNames("card rounded-2xl p-4 flex items-center gap-4", bg)}>
//       <div className="flex-1">
//         <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
//         <div className={classNames("text-3xl font-semibold", color)}>{value}</div>
//       </div>
//     </div>
//   );
// }

// // ---------------------------------
// // Main App Component
// // ---------------------------------
// export default function App() {
//   const [enterprises, setEnterprises] = useState([]);
//   const [query, setQuery] = useState("");
//   const [statusFilter, setStatusFilter] = useState("all");
//   const [defaultId, setDefaultId] = useState(null);
//   const [confirmId, setConfirmId] = useState(null);

//   const [page, setPage] = useState(1);
//   const [perPage] = useState(20);
//   const [totalPages, setTotalPages] = useState(1);
//   const [loading, setLoading] = useState(false);

//   const [granularity, setGranularity] = useState("month");
//   const [selectedMonth, setSelectedMonth] = useState(() =>
//     new Date().toISOString().slice(0, 7)
//   );
//   const [monthPicker, setMonthPicker] = useState(() =>
//     new Date().toISOString().slice(0, 7)
//   );

//   // Fetch data from API
//   useEffect(() => {
//     const fetchEnterprises = async () => {
//       try {
//         setLoading(true);
//         const res = await axios.get(
//           `https://spillas.com:3055/enterprises?page=${page}&per_page=${perPage}`
//         );
//         const { enterprises: data, total_pages } = res.data;

//         const normalized = data.map((e) => ({
//           id: e.id,
//           name: e.enterprise_name,
//           createdAt: e.created_at,
//           lastSyncAt: e.last_sync,
//           role:e.role,
//           totalDevices: e.device_count ?? 0,
//           qrLink: e.qr_url ? `https://spillas.com${e.qr_url}` : "#",
//           relatedType: e.company_name ? "company" : "retailer",
//           relatedName: e.company_name || e.retailer_name || "-",
//           orgName: e.business_name || "-",
//           code: e.retailer_code || e.zcompanyid || "-",
//           status: Number(e.default_enterprise) || 0,
//           email: e.email || "-",
//           phone: e.mobile || "-",
//           password: e.password ? "••••••••" : "-",
//           __isDefault: e.default_enterprise === "1",
//         }));

//         setEnterprises(normalized);
//         setTotalPages(total_pages || 1);
//       } catch (err) {
//         console.error("Failed to fetch enterprises:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchEnterprises();
//   }, [page, perPage]);

//   const filtered = useMemo(() => {
//     return enterprises
//       .map((e) => ({ ...e, __isDefault: e.id === defaultId }))
//       .filter((e) => {
//         const matchText = [
//           e.id,
//           e.relatedName,
//           e.relatedType,
//           e.orgName,
//           e.code,
//           e.email,
//           e.phone,
//         ]
//           .join(" ")
//           .toLowerCase()
//           .includes(query.toLowerCase());

//         const matchStatus =
//           statusFilter === "all"
//             ? true
//             : statusFilter === "inuse"
//             ? e.status === 1
//             : statusFilter === "used"
//             ? e.status === 0
//             : e.status === 2;

//         return matchText && matchStatus;
//       });
//   }, [enterprises, query, statusFilter, defaultId]);

//   const summary = useMemo(() => {
//     const base = enterprises;
//     const by = (pred) => base.filter(pred).length;
//     return {
//       total: base.length,
//       inuse: by((e) => e.status === 1),
//       used: by((e) => e.status === 0),
//       provision: by((e) => e.status === 2),
//       companyInuse: by((e) => e.relatedType === "company" && e.status === 1),
//       companyUsed: by((e) => e.relatedType === "company" && e.status === 0),
//       companyProvision: by((e) => e.relatedType === "company" && e.status === 2),
//       retailerInuse: by((e) => e.relatedType === "retailer" && e.status === 1),
//       retailerUsed: by((e) => e.relatedType === "retailer" && e.status === 0),
//       retailerProvision: by((e) => e.relatedType === "retailer" && e.status === 2),
//     };
//   }, [enterprises]);

//   const monthSeries = useMemo(() => groupByMonth(MOCK_DEACTIVATIONS), []);
//   const daySeries = useMemo(
//     () => groupByDayOfMonth(MOCK_DEACTIVATIONS, selectedMonth),
//     [selectedMonth]
//   );

//   function requestSetDefault(id) {
//     setConfirmId(id);
//   }
//   function confirmSetDefault() {
//     if (confirmId) {
//       setDefaultId(confirmId);
//       setConfirmId(null);
//     }
//   }
//   function cancelSetDefault() {
//     setConfirmId(null);
//   }

//   return (
//     <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] p-6">
//       {/* Global Styles */}
//       <style>{`
//         :root{
//           --bg:#f8fafc; --panel:#ffffff; --panel-2:#f1f5f9; --text:#0f172a;
//           --muted:#475569; --brand:#4f46e5; --accent:#06b6d4; --success:#16a34a;
//           --danger:#dc2626; --warning:#f59e0b; --card:#ffffff; --border:rgba(15,23,42,0.08);
//           --shadow: 0 8px 18px rgba(2,6,23,0.08);
//         }
//         * { font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans'; }
//         .card { background: linear-gradient(180deg, var(--panel), var(--panel-2)); box-shadow: var(--shadow); border: 1px solid var(--border); }
//         .chip { border:1px solid var(--border); }
//         .grid-auto { display:grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem; }
//         .btn { transition: transform .06s ease; } .btn:active { transform: translateY(1px); }
//         .table-wrap { overflow: auto; } .table { min-width: 1200px; } .muted { color: var(--muted); }
//         .badge-default { background: #e0e7ff; color: #3730a3; }
//         .bg-red-soft { background: #fee2e2; }
//       `}</style>

//       {/* Header */}
//       <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
//         <div>
//           <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
//             Enterprise Status Dashboard
//           </h1>
//           <p className="text-sm muted mt-1">
//             Track status (1=In Use, 0=Used, 2=Provision), default selection, and key details
//           </p>
//         </div>
//         <div className="flex flex-wrap gap-2">
//           <input
//             value={query}
//             onChange={(e) => setQuery(e.target.value)}
//             placeholder="Search by ID, email, phone, code..."
//             className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] placeholder:opacity-60"
//           />
//           <select
//             value={statusFilter}
//             onChange={(e) => setStatusFilter(e.target.value)}
//             className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
//             title="Filter by status"
//           >
//             <option value="all">All</option>
//             <option value="inuse">In Use (1)</option>
//             <option value="used">Used (0)</option>
//             <option value="provision">Provision (2)</option>
//           </select>
//           <button
//             onClick={() => downloadCSV(filtered)}
//             className="btn px-3 py-2 rounded-xl bg-[var(--brand)] text-white"
//           >
//             Export CSV
//           </button>
//         </div>
//       </header>

//       {/* KPI Cards */}
//       <section className="grid-auto mb-6">
//         <KPI title="Total" value={summary.total} tone="neutral" />
//         <KPI title="In Use (All)" value={summary.inuse} tone="success" />
//         <KPI title="Used (All)" value={summary.used} tone="neutral" />
//         <KPI title="Provision (All)" value={summary.provision} tone="warning" />
//         <KPI title="Company — In Use" value={summary.companyInuse} tone="success" />
//         <KPI title="Company — Used" value={summary.companyUsed} tone="neutral" />
//         <KPI title="Company — Provision" value={summary.companyProvision} tone="warning" />
//         <KPI title="Retailer — In Use" value={summary.retailerInuse} tone="success" />
//         <KPI title="Retailer — Used" value={summary.retailerUsed} tone="neutral" />
//         <KPI title="Retailer — Provision" value={summary.retailerProvision} tone="warning" />
//       </section>

//       {/* Chart Panel */}
//       <section className="card rounded-2xl p-5 mb-6">
//         <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-4">
//           <div>
//             <h2 className="text-lg font-medium">Deactivated Enterprises</h2>
//             <p className="text-sm muted">Visualize counts by month or by day within a selected month.</p>
//           </div>
//           <div className="flex flex-wrap items-center gap-2">
//             <div className="chip rounded-xl px-2 py-1 text-xs flex items-center gap-2 bg-[var(--panel)]">
//               <span className="opacity-75">View</span>
//               <button
//                 className={classNames(
//                   "btn px-2 py-1 rounded-lg",
//                   granularity === "month" ? "bg-[var(--brand)] text-white" : "bg-[var(--panel-2)]"
//                 )}
//                 onClick={() => setGranularity("month")}
//               >
//                 Month
//               </button>
//               <button
//                 className={classNames(
//                   "btn px-2 py-1 rounded-lg",
//                   granularity === "day" ? "bg-[var(--brand)] text-white" : "bg-[var(--panel-2)]"
//                 )}
//                 onClick={() => setGranularity("day")}
//               >
//                 Day
//               </button>
//             </div>

//             {granularity === "day" ? (
//               <input
//                 type="month"
//                 value={monthPicker}
//                 onChange={(e) => {
//                   setMonthPicker(e.target.value);
//                   setSelectedMonth(e.target.value);
//                 }}
//                 className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
//                 title="Pick month"
//               />
//             ) : null}
//           </div>
//         </div>

//         <div className="h-72 w-full">
//           <ResponsiveContainer width="100%" height="100%">
//             <BarChart data={granularity === "month" ? monthSeries : daySeries}>
//               <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,6,23,0.1)" />
//               <XAxis dataKey="label" tick={{ fill: "#475569" }} stroke="#cbd5e1" />
//               <YAxis allowDecimals={false} tick={{ fill: "#475569" }} stroke="#cbd5e1" />
//               <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }} />
//               <Bar dataKey="count" fill="var(--accent)" radius={[6, 6, 0, 0]} />
//             </BarChart>
//           </ResponsiveContainer>
//         </div>
//       </section>

//       {/* Pagination Controls */}
//       <div className="flex justify-end items-center gap-3 mb-3">
//         <button
//           onClick={() => setPage((p) => Math.max(1, p - 1))}
//           disabled={page === 1 || loading}
//           className="px-3 py-1 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] disabled:opacity-50"
//         >
//           Prev
//         </button>
//         <span className="text-sm muted">
//           Page {page} of {totalPages}
//         </span>
//         <button
//           onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
//           disabled={page >= totalPages || loading}
//           className="px-3 py-1 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] disabled:opacity-50"
//         >
//           Next
//         </button>
//       </div>

//       {/* Table */}
//      <section className="card rounded-2xl p-5">
//       <div className="overflow-x-auto">
//         <table className="table w-full text-sm min-w-[1000px]">
//           <thead>
//             <tr className="text-left muted border-b border-[var(--border)]">
//               <th className="py-1 pr-4">ID</th>
//               <th className="py-1 pr-4">Code</th>
//               <th className="py-1 pr-4">Related To</th>
//               <th className="py-1 pr-4">Phone</th>
//               <th className="py-1 pr-4">Email</th>
//               <th className="py-1 pr-4">Password</th>
//               <th className="py-1 pr-4">Enterprise Name</th>
//               <th className="py-1 pr-4">Status</th>
//               <th className="py-1 pr-4">Created At</th>
//               <th className="py-1 pr-4">Last Sync</th>
//               <th className="py-1 pr-4">Total Devices</th>
//               <th className="py-1 pr-4">Default</th>
//               <th className="py-2 pr-4">QR Link</th>
//             </tr>
//           </thead>
//           <tbody>
//             {filtered.map((e) => {
//               const highlight = shouldHighlightRow(e);
//               const defaultBadge = e.id === defaultId;
//               return (
//                 <tr
//                   key={e.id}
//                   className={`border-b border-[var(--border)] hover:bg-[var(--panel-2)]/50 ${
//                     highlight ? "bg-red-soft" : ""
//                   }`}
//                 >
//                   <td className="py-2 pr-4 opacity-80">{e.id}</td>
//                   <td className="py-2 pr-4">{e.code}</td>
//                   <td className="py-2 pr-4">
//                     <span className="opacity-70 mr-1">
//                       {e.role === 5 ? "Retailer:" : e.role === 2 ? "Company:" : "Unknown:"}
//                     </span>
//                     <span className="font-medium">{e.relatedName}</span>
//                   </td>
//                   <td className="py-2 pr-4 opacity-80">{e.phone}</td>
//                   <td className="py-2 pr-4 opacity-80">{e.email}</td>
//                   <td className="py-2 pr-4 opacity-80">{e.password}</td>
//                   <td className="py-2 pr-4 opacity-80">{e.name}</td>
//                   <td className="py-2 pr-4">
//                     <span className={`px-2 py-1 rounded-lg text-xs ${statusClass(e.status)}`}>
//                       {statusLabel(e.status)}
//                     </span>
//                   </td>
//                   <td className="py-2 pr-4 opacity-80">{toLocal(e.createdAt)}</td>
//                   <td className="py-2 pr-4 opacity-80">{toLocal(e.lastSyncAt)}</td>
//                   <td className="py-2 pr-4 font-semibold">{e.totalDevices}</td>
//                   <td className="py-2 pr-4">
//                     {defaultBadge ? (
//                       <span className="badge-default px-2 py-1 rounded text-xs">Default</span>
//                     ) : (
//                       <button
//                         onClick={() => requestSetDefault(e.id)}
//                         className="btn px-2 py-1 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] hover:bg-[var(--panel-2)]/80 text-xs"
//                       >
//                         Set default
//                       </button>
//                     )}
//                   </td>
//                   <td className="py-2 pr-4">
//                     <a
//                       href={e.qrLink}
//                       target="_blank"
//                       rel="noreferrer"
//                       className="underline decoration-dotted hover:opacity-80"
//                     >
//                       Open
//                     </a>
//                   </td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>
//       {loading && <p className="mt-2 text-sm muted">Loading...</p>}
//     </section>


//       {confirmId && (
//         <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
//           <div className="bg-white rounded-2xl shadow-lg border border-[var(--border)] w-[min(95vw,480px)] p-5">
//             <h3 className="text-lg font-semibold mb-2">Set as default?</h3>
//             <p className="text-sm text-slate-600 mb-4">
//               You're about to set <span className="font-medium">{confirmId}</span> as your default enterprise. Continue?
//             </p>
//             <div className="flex justify-end gap-2">
//               <button onClick={cancelSetDefault} className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)]">Cancel</button>
//               <button onClick={confirmSetDefault} className="px-3 py-2 rounded-xl bg-[var(--brand)] text-white">Yes, set default</button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }




// import React, { useEffect, useMemo, useState } from "react";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip,
//   CartesianGrid,
//   ResponsiveContainer,
// } from "recharts";

// /**
//  * Enterprise Status Dashboard — Single Page App (Light Theme)
//  * Tech: React (JS), Tailwind CSS, Recharts
//  *
//  * Fix: Resolved SyntaxError (Unterminated string constant) by correctly escaping newlines
//  * in CSV export and fixing the regex literal. Also removed a previously duplicated module
//  * import block that could cause redeclarations.
//  *
//  * Features (as requested):
//  * - Light color scheme
//  * - Table includes Email, Phone, Password (masked sample)
//  * - Status mapping: 1 = In Use, 0 = Used, 2 = Provision
//  * - Remove Enterprise Name column
//  * - "Set Default" action with confirmation popup; shows which is default
//  * - Highlight Total Devices in light red when > 300 **and** status = In Use
//  * - Deactivated chart with Month/Day toggle + month picker
//  * - CSV export (includes Default flag + Status label)
//  */

// // ---------------------------------
// // Mock Types & Data
// // ---------------------------------
// /**
//  * status: 0 = used, 1 = in use, 2 = provision
//  */
// const MOCK_ENTERPRISES = [
//   {
//     id: "ENT-1001",
//     name: "Alpha Retail Hub",
//     createdAt: "2025-01-07T10:15:00Z",
//     lastSyncAt: "2025-10-05T12:45:00Z",
//     totalDevices: 142,
//     qrLink: "https://example.com/q/alpha",
//     relatedType: "retailer",
//     relatedName: "Alpha Mart",
//     orgName: "Alpha Org",
//     code: "ALP-01",
//     status: 1, // In Use
//     email: "alpha@example.com",
//     phone: "+91-90000-00001",
//     password: "••••••••",
//   },
//   {
//     id: "ENT-1002",
//     name: "Beta Enterprise",
//     createdAt: "2024-11-18T09:00:00Z",
//     lastSyncAt: "2025-08-22T18:22:00Z",
//     totalDevices: 58,
//     qrLink: "https://example.com/q/beta",
//     relatedType: "company",
//     relatedName: "Beta Co.",
//     orgName: "Beta Group",
//     code: "BET-11",
//     status: 0, // Used
//     email: "beta@example.com",
//     phone: "+91-90000-00002",
//     password: "••••••••",
//   },
//   {
//     id: "ENT-1003",
//     name: "Gamma Wholesale",
//     createdAt: "2025-03-03T08:32:00Z",
//     lastSyncAt: "2025-10-02T14:03:00Z",
//     totalDevices: 301,
//     qrLink: "https://example.com/q/gamma",
//     relatedType: "company",
//     relatedName: "Gamma Holdings",
//     orgName: "Gamma Org",
//     code: "GAM-07",
//     status: 1, // In Use
//     email: "gamma@example.com",
//     phone: "+91-90000-00003",
//     password: "••••••••",
//   },
//   {
//     id: "ENT-1004",
//     name: "Delta Stores",
//     createdAt: "2025-05-12T11:21:00Z",
//     lastSyncAt: "2025-09-12T07:33:00Z",
//     totalDevices: 87,
//     qrLink: "https://example.com/q/delta",
//     relatedType: "retailer",
//     relatedName: "Delta Bazaar",
//     orgName: "Delta Org",
//     code: "DEL-04",
//     status: 2, // Provision (test case)
//     email: "delta@example.com",
//     phone: "+91-90000-00004",
//     password: "••••••••",
//   },
//   {
//     id: "ENT-1005",
//     name: "Epsilon Partners",
//     createdAt: "2025-02-22T16:04:00Z",
//     lastSyncAt: "2025-10-08T19:15:00Z",
//     totalDevices: 450,
//     qrLink: "https://example.com/q/epsilon",
//     relatedType: "company",
//     relatedName: "Epsilon Pvt Ltd",
//     orgName: "Epsilon Org",
//     code: "EPS-21",
//     status: 1, // In Use & > 300 (highlight rule)
//     email: "epsilon@example.com",
//     phone: "+91-90000-00005",
//     password: "••••••••",
//   },
// ];

// // Simulated historical deactivation events for charts (date strings in ISO)
// const MOCK_DEACTIVATIONS = [
//   "2025-01-15", "2025-01-21", "2025-02-02", "2025-02-19",
//   "2025-03-05", "2025-03-05", "2025-03-18",
//   "2025-04-01", "2025-04-15", "2025-04-30",
//   "2025-05-07",
//   "2025-06-11", "2025-06-27",
//   "2025-07-03", "2025-07-19", "2025-07-28",
//   "2025-08-04",
//   "2025-09-09",
//   "2025-10-01", "2025-10-01", "2025-10-09"
// ];

// // ---------------------------------
// // Utilities
// // ---------------------------------
// const classNames = (...xs) => xs.filter(Boolean).join(" ");
// const toLocal = (iso) => new Date(iso).toLocaleString();

// const statusLabel = (s) => (s === 1 ? "In Use" : s === 0 ? "Used" : "Provision");
// const statusClass = (s) =>
//   s === 1
//     ? "bg-green-100 text-green-700"
//     : s === 0
//     ? "bg-slate-200 text-slate-700"
//     : "bg-amber-100 text-amber-700";

// // CSV helpers are hoisted so we can test them
// function escapeCSV(s) {
//   const v = String(s ?? "");
//   return /[",\n]/.test(v) ? '"' + v.replaceAll('"', '""') + '"' : v;
// }

// function downloadCSV(rows, filename = "enterprises.csv") {
//      const headers = [
//     "Default",
//     "Status(0/1/2)",
//     "Status Label",
//     "ID",
//     "Email",
//     "Phone",
//     "Password",
//     "Created At",
//     "Last Sync",
//     "Total Devices",
//     "QR Link",
//     "Related Type",
//     "Related Name",
//     "Org Name",
//     "Code",
//   ];
//   const out = [headers.join(",")].concat(
//     rows.map((e) =>
//       [
//         e.__isDefault ? "YES" : "",
//         e.status,
//         statusLabel(e.status),
//         escapeCSV(e.id),
//         escapeCSV(e.email),
//         escapeCSV(e.phone),
//         escapeCSV(e.password),
//         escapeCSV(toLocal(e.createdAt)),
//         escapeCSV(toLocal(e.lastSyncAt)),
//         e.totalDevices,
//         escapeCSV(e.qrLink),
//         escapeCSV(e.relatedType),
//         escapeCSV(e.relatedName),
//         escapeCSV(e.orgName),
//         escapeCSV(e.code),
//       ].join(",")
//     )
//   );
//   const blob = new Blob(["\uFEFF" + out.join("\n")], { type: "text/csv;charset=utf-8;" });
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.href = url;
//   a.download = filename;
//   a.click();
//   URL.revokeObjectURL(url);
// }

// // Group by month: returns [{ label: '2025-01', count: 3 }, ...]
// function groupByMonth(dates) {
//   const map = new Map();
//   dates.forEach((d) => {
//     const month = d.slice(0, 7); // YYYY-MM
//     map.set(month, (map.get(month) || 0) + 1);
//   });
//   return Array.from(map.entries()).map(([label, count]) => ({ label, count }));
// }

// // Group by day within a specific month (YYYY-MM)
// function groupByDayOfMonth(dates, month) {
//   const inMonth = dates.filter((d) => d.startsWith(month));
//   const map = new Map();
//   inMonth.forEach((d) => {
//     const day = d.slice(8, 10); // DD
//     map.set(day, (map.get(day) || 0) + 1);
//   });
//   // Fill missing days for a smoother chart
//   const [y, m] = month.split("-").map(Number);
//   const daysInMonth = new Date(y, m, 0).getDate();
//   const out = [];
//   for (let i = 1; i <= daysInMonth; i++) {
//     const dd = String(i).padStart(2, "0");
//     out.push({ label: dd, count: map.get(dd) || 0 });
//   }
//   return out;
// }

// // Row highlight rule (testable)
// function shouldHighlightRow(e) {
//   return e.status === 1 && Number(e.totalDevices) > 300;
// }

// // ---------------------------------
// // Main Component
// // ---------------------------------
// export default function App() {
//   const [enterprises, setEnterprises] = useState([]);
//   const [query, setQuery] = useState("");
//   const [statusFilter, setStatusFilter] = useState("all"); // all | inuse | used | provision
//   const [defaultId, setDefaultId] = useState(null); // id of default enterprise
//   const [confirmId, setConfirmId] = useState(null); // id pending confirmation

//   // Chart controls
//   const [granularity, setGranularity] = useState("month"); // month | day
//   const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
//   const [monthPicker, setMonthPicker] = useState(() => new Date().toISOString().slice(0, 7));

//   // Simulate fetch on mount
//   useEffect(() => {
//     // TODO: Replace with real API call
//     setEnterprises(MOCK_ENTERPRISES);
//   }, []);

//   const filtered = useMemo(() => {
//     return enterprises
//       .map((e) => ({ ...e, __isDefault: e.id === defaultId }))
//       .filter((e) => {
//         const matchText = [
//           e.id,
//           e.relatedName,
//           e.relatedType,
//           e.orgName,
//           e.code,
//           e.email,
//           e.phone,
//         ]
//           .join(" ")
//           .toLowerCase()
//           .includes(query.toLowerCase());

//         const matchStatus =
//           statusFilter === "all"
//             ? true
//             : statusFilter === "inuse"
//             ? e.status === 1
//             : statusFilter === "used"
//             ? e.status === 0
//             : e.status === 2; // provision

//         return matchText && matchStatus;
//       });
//   }, [enterprises, query, statusFilter, defaultId]);

//   const summary = useMemo(() => {
//     const base = enterprises;
//     const by = (pred) => base.filter(pred).length;
//     const inuse = by((e) => e.status === 1);
//     const used = by((e) => e.status === 0);
//     const provision = by((e) => e.status === 2);

//     const companyInuse = by((e) => e.relatedType === "company" && e.status === 1);
//     const companyUsed = by((e) => e.relatedType === "company" && e.status === 0);
//     const companyProvision = by((e) => e.relatedType === "company" && e.status === 2);

//     const retailerInuse = by((e) => e.relatedType === "retailer" && e.status === 1);
//     const retailerUsed = by((e) => e.relatedType === "retailer" && e.status === 0);
//     const retailerProvision = by((e) => e.relatedType === "retailer" && e.status === 2);

//     return {
//       total: base.length,
//       inuse,
//       used,
//       provision,
//       companyInuse,
//       companyUsed,
//       companyProvision,
//       retailerInuse,
//       retailerUsed,
//       retailerProvision,
//     };
//   }, [enterprises]);

//   // Prepare chart data
//   const monthSeries = useMemo(() => groupByMonth(MOCK_DEACTIVATIONS), []);
//   const daySeries = useMemo(() => groupByDayOfMonth(MOCK_DEACTIVATIONS, selectedMonth), [selectedMonth]);

//   function requestSetDefault(id) {
//     setConfirmId(id);
//   }

//   function confirmSetDefault() {
//     if (confirmId) {
//       setDefaultId(confirmId);
//       setConfirmId(null);
//     }
//   }

//   function cancelSetDefault() {
//     setConfirmId(null);
//   }

//   return (
//     <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] p-6">
//       {/* Global Styles */}
//       <style>{`
//         :root{
//           /* LIGHT THEME */
//           --bg:#f8fafc;           /* slate-50 */
//           --panel:#ffffff;        /* white */
//           --panel-2:#f1f5f9;      /* slate-100 */
//           --text:#0f172a;         /* slate-900 */
//           --muted:#475569;        /* slate-600 */
//           --brand:#4f46e5;        /* indigo-600 */
//           --accent:#06b6d4;       /* cyan-500 */
//           --success:#16a34a;      /* green-600 */
//           --danger:#dc2626;       /* red-600 */
//           --warning:#f59e0b;      /* amber-500 */
//           --card:#ffffff;
//           --border:rgba(15,23,42,0.08);
//           --shadow: 0 8px 18px rgba(2,6,23,0.08);
//         }
//         * { font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji'; }
//         .card { background: linear-gradient(180deg, var(--panel), var(--panel-2)); box-shadow: var(--shadow); border: 1px solid var(--border); }
//         .chip { border:1px solid var(--border); }
//         .grid-auto { display:grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem; }
//         .btn { transition: transform .06s ease; }
//         .btn:active { transform: translateY(1px); }
//         .table-wrap { overflow: auto; }
//         .table { min-width: 1200px; }
//         .muted { color: var(--muted); }
//         .badge-default { background: #e0e7ff; color: #3730a3; } /* indigo-50/800 */
//         .bg-red-soft { background: #fee2e2; } /* rose-100 */
//       `}</style>

//       {/* Header */}
//       <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
//         <div>
//           <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
//             Enterprise Status Dashboard
//           </h1>
//           <p className="text-sm muted mt-1">
//             Track status (1=In Use, 0=Used, 2=Provision), default selection, and key details
//           </p>
//         </div>
//         <div className="flex flex-wrap gap-2">
//           <input
//             value={query}
//             onChange={(e) => setQuery(e.target.value)}
//             placeholder="Search by ID, email, phone, code..."
//             className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] placeholder:opacity-60"
//           />
//           <select
//             value={statusFilter}
//             onChange={(e) => setStatusFilter(e.target.value)}
//             className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
//             title="Filter by status"
//           >
//             <option value="all">All</option>
//             <option value="inuse">In Use (1)</option>
//             <option value="used">Used (0)</option>
//             <option value="provision">Provision (2)</option>
//           </select>
//           <button
//             onClick={() => downloadCSV(filtered)}
//             className="btn px-3 py-2 rounded-xl bg-[var(--brand)] text-white"
//           >
//             Export CSV
//           </button>
//         </div>
//       </header>

//       {/* KPI Cards */}
//       <section className="grid-auto mb-6">
//         <KPI title="Total" value={summary.total} tone="neutral" />
//         <KPI title="In Use (All)" value={summary.inuse} tone="success" />
//         <KPI title="Used (All)" value={summary.used} tone="neutral" />
//         <KPI title="Provision (All)" value={summary.provision} tone="warning" />
//         <KPI title="Company — In Use" value={summary.companyInuse} tone="success" />
//         <KPI title="Company — Used" value={summary.companyUsed} tone="neutral" />
//         <KPI title="Company — Provision" value={summary.companyProvision} tone="warning" />
//         <KPI title="Retailer — In Use" value={summary.retailerInuse} tone="success" />
//         <KPI title="Retailer — Used" value={summary.retailerUsed} tone="neutral" />
//         <KPI title="Retailer — Provision" value={summary.retailerProvision} tone="warning" />
//       </section>

//       {/* Chart Panel */}
//       <section className="card rounded-2xl p-5 mb-6">
//         <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-4">
//           <div>
//             <h2 className="text-lg font-medium">Deactivated Enterprises</h2>
//             <p className="text-sm muted">Visualize counts by month or by day within a selected month.</p>
//           </div>
//           <div className="flex flex-wrap items-center gap-2">
//             <div className="chip rounded-xl px-2 py-1 text-xs flex items-center gap-2 bg-[var(--panel)]">
//               <span className="opacity-75">View</span>
//               <button
//                 className={classNames(
//                   "btn px-2 py-1 rounded-lg",
//                   granularity === "month" ? "bg-[var(--brand)] text-white" : "bg-[var(--panel-2)]"
//                 )}
//                 onClick={() => setGranularity("month")}
//               >
//                 Month
//               </button>
//               <button
//                 className={classNames(
//                   "btn px-2 py-1 rounded-lg",
//                   granularity === "day" ? "bg-[var(--brand)] text-white" : "bg-[var(--panel-2)]"
//                 )}
//                 onClick={() => setGranularity("day")}
//               >
//                 Day
//               </button>
//             </div>

//             {granularity === "day" ? (
//               <input
//                 type="month"
//                 value={monthPicker}
//                 onChange={(e) => {
//                   setMonthPicker(e.target.value);
//                   setSelectedMonth(e.target.value);
//                 }}
//                 className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
//                 title="Pick month"
//               />
//             ) : null}
//           </div>
//         </div>

//         <div className="h-72 w-full">
//           <ResponsiveContainer width="100%" height="100%">
//             <BarChart data={granularity === "month" ? monthSeries : daySeries}>
//               <CartesianGrid strokeDasharray="3 3" stroke="rgba(2,6,23,0.1)" />
//               <XAxis dataKey="label" tick={{ fill: "#475569" }} stroke="#cbd5e1" />
//               <YAxis allowDecimals={false} tick={{ fill: "#475569" }} stroke="#cbd5e1" />
//               <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }} />
//               <Bar dataKey="count" fill="var(--accent)" radius={[6, 6, 0, 0]} />
//             </BarChart>
//           </ResponsiveContainer>
//         </div>
//       </section>

//       {/* Enterprises Table */}
//       <section className="card rounded-2xl p-5">
//         <div className="flex items-center justify-between mb-4">
//           <h2 className="text-lg font-medium">Enterprises</h2>
//           <span className="text-xs muted">
//             Showing {filtered.length} of {enterprises.length}
//           </span>
//         </div>
        // <div className="table-wrap">
        //   <table className="table w-full text-sm">
        //     <thead>
        //       <tr className="text-left muted border-b border-[var(--border)]">
        //         <th className="py-2 pr-4">Default</th>
        //         <th className="py-2 pr-4">Status</th>
        //         {/* Name column intentionally removed */}
        //         <th className="py-2 pr-4">ID</th>
        //         <th className="py-2 pr-4">Email</th>
        //         <th className="py-2 pr-4">Phone</th>
        //         <th className="py-2 pr-4">Password</th>
        //         <th className="py-2 pr-4">Created At</th>
        //         <th className="py-2 pr-4">Last Sync</th>
        //         <th className="py-2 pr-4">Total Devices</th>
        //         <th className="py-2 pr-4">QR Link</th>
        //         <th className="py-2 pr-4">Related To</th>
        //         <th className="py-2 pr-4">Org Name</th>
        //         <th className="py-2 pr-4">Code</th>
        //       </tr>
        //     </thead>
        //     <tbody>
        //       {filtered.map((e) => {
        //         const highlight = shouldHighlightRow(e);
        //         const defaultBadge = e.id === defaultId;
        //         return (
        //           <tr
        //             key={e.id}
        //             className={classNames(
        //               "border-b border-[var(--border)] hover:bg-[var(--panel-2)]/50",
        //               highlight && "bg-red-soft"
        //             )}
        //           >
        //             <td className="py-2 pr-4">
        //               {defaultBadge ? (
        //                 <span className="badge-default px-2 py-1 rounded text-xs">Default</span>
        //               ) : (
        //                 <button
        //                   onClick={() => requestSetDefault(e.id)}
        //                   className="btn px-2 py-1 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] hover:bg-[var(--panel-2)]/80 text-xs"
        //                 >
        //                   Set default
        //                 </button>
        //               )}
        //             </td>
        //             <td className="py-2 pr-4">
        //               <span className={classNames("px-2 py-1 rounded-lg text-xs", statusClass(e.status))}>
        //                 {statusLabel(e.status)}
        //               </span>
        //             </td>
        //             {/* Name removed */}
        //             <td className="py-2 pr-4 opacity-80">{e.id}</td>
        //             <td className="py-2 pr-4 opacity-80">{e.email}</td>
        //             <td className="py-2 pr-4 opacity-80">{e.phone}</td>
        //             <td className="py-2 pr-4 opacity-80">{e.password}</td>
        //             <td className="py-2 pr-4 opacity-80">{toLocal(e.createdAt)}</td>
        //             <td className="py-2 pr-4 opacity-80">{toLocal(e.lastSyncAt)}</td>
        //             <td className="py-2 pr-4 font-semibold">{e.totalDevices}</td>
        //             <td className="py-2 pr-4">
        //               <a
        //                 href={e.qrLink}
        //                 target="_blank"
        //                 rel="noreferrer"
        //                 className="underline decoration-dotted hover:opacity-80"
        //               >
        //                 Open
        //               </a>
        //             </td>
        //             <td className="py-2 pr-4">
        //               <span className="opacity-70 mr-1">{e.relatedType === "company" ? "Company:" : "Retailer:"}</span>
        //               <span className="font-medium">{e.relatedName}</span>
        //             </td>
        //             <td className="py-2 pr-4">{e.orgName}</td>
        //             <td className="py-2 pr-4">{e.code}</td>
        //           </tr>
        //         );
        //       })}
        //     </tbody>
        //   </table>
        // </div>
//       </section>

//       {/* Confirm Default Modal */}
      // {confirmId && (
      //   <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      //     <div className="bg-white rounded-2xl shadow-lg border border-[var(--border)] w-[min(95vw,480px)] p-5">
      //       <h3 className="text-lg font-semibold mb-2">Set as default?</h3>
      //       <p className="text-sm text-slate-600 mb-4">
      //         You're about to set <span className="font-medium">{confirmId}</span> as your default enterprise. Continue?
      //       </p>
      //       <div className="flex justify-end gap-2">
      //         <button onClick={cancelSetDefault} className="px-3 py-2 rounded-xl bg-[var(--panel-2)] border border-[var(--border)]">Cancel</button>
      //         <button onClick={confirmSetDefault} className="px-3 py-2 rounded-xl bg-[var(--brand)] text-white">Yes, set default</button>
      //       </div>
      //     </div>
      //   </div>
      // )}

//       {/* Footer */}
//       <footer className="mt-8 text-center text-xs muted">
//         <p>
//           Status key: <span className="text-green-700">1 = In Use</span>, <span className="text-slate-600">0 = Used</span>, <span className="text-amber-600">2 = Provision</span>
//         </p>
//       </footer>
//     </div>
//   );
// }

// function KPI({ title, value, tone = "neutral" }) {
//   const color =
//     tone === "success" ? "text-green-700" : tone === "danger" ? "text-red-700" : tone === "warning" ? "text-amber-700" : "text-cyan-700";
//   const bg =
//     tone === "success" ? "bg-green-100" : tone === "danger" ? "bg-red-100" : tone === "warning" ? "bg-amber-100" : "bg-cyan-100";
//   return (
//     <div className={classNames("card rounded-2xl p-4 flex items-center gap-4", bg)}>
//       <div className="flex-1">
//         <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
//         <div className={classNames("text-3xl font-semibold", color)}>{value}</div>
//       </div>
//     </div>
//   );
// }

// // ---------------------------------
// // Tests (dev-only)
// // ---------------------------------
// (function runDevTests(){
//   try {
//     // Only run in browser/dev contexts
//     if (typeof window === "undefined") return;

//     // Test 1: statusLabel mapping
//     console.assert(statusLabel(1) === "In Use", "statusLabel(1) should be 'In Use'");
//     console.assert(statusLabel(0) === "Used", "statusLabel(0) should be 'Used'");
//     console.assert(statusLabel(2) === "Provision", "statusLabel(2) should be 'Provision'");

//     // Test 2: highlight rule
//     console.assert(shouldHighlightRow({ status: 1, totalDevices: 301 }) === true, 
//       "Highlight when In Use & > 300");
//     console.assert(shouldHighlightRow({ status: 1, totalDevices: 300 }) === false, 
//       "No highlight at 300 exactly");
//     console.assert(shouldHighlightRow({ status: 0, totalDevices: 500 }) === false, 
//       "No highlight when not In Use");

//     // Test 3: CSV escaping
//     console.assert(escapeCSV('a,b') === '"a,b"', "CSV should quote commas");
//     console.assert(escapeCSV('He said "Hi"') === '"He said ""Hi"""', "CSV should double quotes");
//     console.assert(escapeCSV('line1\nline2') === '"line1\nline2"', "CSV should quote newlines");

//     // Test 4: groupByDayOfMonth length (Feb 2025 has 28 days)
//     const febDays = groupByDayOfMonth(["2025-02-01", "2025-02-28"], "2025-02");
//     console.assert(Array.isArray(febDays) && febDays.length === 28, "February 2025 should have 28 days");

//     console.debug("✅ Dev tests passed");
//   } catch (err) {
//     console.warn("⚠️ Dev tests encountered an error", err);
//   }
// })();
